/**
 * The "Saved" list in the Filters panel (#2291): every saved search,
 * including ones not on Discover. Clicking one runs it; each can be put on
 * or taken off Discover, renamed (title and description; the query stays),
 * or deleted. Deleting one that a Discover section shows asks first, in
 * the list, then removes both in one write.
 */
import { useId, useState } from 'react';
import { DISCOVER_LIMITS, type DiscoverLayout, type SavedSearch } from '@/api/discoverPreferences';
import {
  deleteSearch,
  isOnDiscover,
  removeSearchFromDiscover,
  renameSearch,
  sectionsOfSearch,
  showSearch,
  type LayoutChange,
} from './discoverLayout';
import { outcomeMessages, type WriteOutcome } from './discoverLayoutStore';

interface Props {
  layout: DiscoverLayout;
  apply: (change: LayoutChange) => Promise<WriteOutcome>;
  onRun: (search: SavedSearch) => void;
  /** The search the box holds now, when it is one of these. */
  currentId?: string | null;
  /** Says what went wrong with a change made from a row (not a form). */
  onError: (messages: string[]) => void;
}

function RenameForm({ search, apply, onDone }: Readonly<{
  search: SavedSearch;
  apply: Props['apply'];
  onDone: () => void;
}>) {
  const titleId = useId();
  const descId = useId();
  const [title, setTitle] = useState(search.title);
  const [description, setDescription] = useState(search.description ?? '');
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const bad = /</.test(title) || /</.test(description);
  return (
    <form
      className="dsq-saved-rename"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy || !title.trim() || bad) return;
        setBusy(true);
        const outcome = await apply(renameSearch(search.id, title, description));
        setBusy(false);
        if (outcome.ok) onDone();
        else setProblems(outcomeMessages(outcome));
      }}
    >
      <div className="dsq-save-labelrow">
        <label className="dsq-panel-label" htmlFor={titleId}>Title</label>
        <span className="dsq-counter" aria-hidden="true">{title.length}/{DISCOVER_LIMITS.title}</span>
      </div>
      <input id={titleId} className="dsq-panel-input" value={title} maxLength={DISCOVER_LIMITS.title} autoFocus // NOSONAR typescript:S9379 accessibility-by-design: dialog autoFocus (primary control focused on open)
             onChange={(e) => setTitle(e.target.value)}/>
      <div className="dsq-save-labelrow">
        <label className="dsq-panel-label" htmlFor={descId}>Description</label>
        <span className="dsq-counter" aria-hidden="true">{description.length}/{DISCOVER_LIMITS.description}</span>
      </div>
      <textarea id={descId} className="dsq-panel-input dsq-textarea" rows={2} value={description}
                maxLength={DISCOVER_LIMITS.description} onChange={(e) => setDescription(e.target.value)}/>
      {(bad || problems.length > 0) && (
        <ul className="dsq-problems" role="alert">
          {bad && <li>Titles and descriptions can't contain &lt;.</li>}
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      <div className="flex gap-3 items-center">
        <button type="submit" className="dsq-link" disabled={busy || !title.trim() || bad}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="dsq-link" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

export default function SavedSearchList({ layout, apply, onRun, currentId, onError }: Readonly<Props>) {
  const headId = useId();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const run = (change: LayoutChange) => {
    void apply(change).then((outcome) => { if (!outcome.ok) onError(outcomeMessages(outcome)); });
  };
  const remove = (s: SavedSearch) => {
    if (sectionsOfSearch(layout, s.id).length > 0) setConfirming(s.id);
    else run(deleteSearch(s.id));
  };

  return (
    <div className="dsq-panel-field is-full dsq-saved" data-field="saved">
      <span className="dsq-panel-label" id={headId}>Saved</span>
      {layout.searches.length === 0 ? (
        <p className="dsq-saved-empty">Searches you save show up here. Use “☆ Save search” on any search.</p>
      ) : (
        <ul className="dsq-saved-list" aria-labelledby={headId}>
          {layout.searches.map((s) => {
            const on = isOnDiscover(layout, s.id);

            const renderEntry = () => {
              if (renaming === s.id) {
                return <RenameForm search={s} apply={apply} onDone={() => setRenaming(null)}/>;
              }
              if (confirming === s.id) {
                return (
                  <div className="dsq-saved-confirm" role="alert">
                    <span>Delete “{s.title}”? It is also a section on Discover, and both will go.</span>
                    <div className="flex gap-3">
                      <button type="button" className="dsq-link is-danger" onClick={() => { setConfirming(null); run(deleteSearch(s.id)); }}>
                        Delete both
                      </button>
                      <button type="button" className="dsq-link" onClick={() => setConfirming(null)}>Cancel</button>
                    </div>
                  </div>
                );
              }
              return (
                <>
                  <button type="button" className="dsq-saved-run" onClick={() => onRun(s)}
                          aria-current={s.id === currentId ? 'true' : undefined}>
                    <span className="dsq-saved-title">{s.title}</span>
                    {s.description && <span className="dsq-saved-desc">{s.description}</span>}
                    {on && <span className="dsq-saved-on">On Discover</span>}
                  </button>
                  <div className="dsq-saved-actions">
                    <button type="button" className="dsq-link"
                            aria-label={`${on ? 'Remove from Discover' : 'Show on Discover'}: ${s.title}`}
                            onClick={() => run(on ? removeSearchFromDiscover(s.id) : showSearch(s.id))}>
                      {on ? 'Remove from Discover' : 'Show on Discover'}
                    </button>
                    <button type="button" className="dsq-link" aria-label={`Rename ${s.title}`}
                            onClick={() => { setConfirming(null); setRenaming(s.id); }}>
                      Rename
                    </button>
                    <button type="button" className="dsq-link is-danger" aria-label={`Delete ${s.title}`}
                            onClick={() => remove(s)}>
                      Delete
                    </button>
                  </div>
                </>
              );
            };

            return (
              <li key={s.id} className={`dsq-saved-item${s.id === currentId ? ' is-current' : ''}`}>
                {renderEntry()}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
